/**
 * The "check your inbox" row for the Integrations panel.
 *
 * A newly onboarded brand is invited to its shared Slack channel over Connect.
 * Nothing in the product told the client that, so they never knew to accept.
 * This derives the row from the server's invite flag - never re-deriving the
 * rule, only presenting what the server recorded.
 *
 * The Trello board invite this row once also reported is gone (spec
 * 2026-10-28, "Client Work view shows the portal lifecycle read-only"): the
 * client no longer gets board access through the portal, so there is nothing
 * Trello-shaped left to report here.
 *
 * Returns null when no invite has gone out, so the row hides itself rather than
 * claiming an invite that never happened (fail-closed, like the other rows).
 *
 * @module web/screens/app/overview/clientInvites
 */

export interface ClientInviteState {
  slackInvited: boolean;
  email: string;
}

export interface ClientInviteRow {
  desc: string;
  status: string;
}

/**
 * The row content, or null to hide it. Pure: same inputs → same row, so a Vitest
 * check pins the copy and the hide rule without a browser.
 */
export function clientInviteRow(brand: ClientInviteState | null | undefined): ClientInviteRow | null {
  if (!brand) return null;
  const { slackInvited, email } = brand;
  if (!slackInvited) return null;

  const to = email ? ` at ${email}` : "";
  return {
    desc: `We've invited you to Slack${to} - check your inbox to join the channel.`,
    status: "Slack invited",
  };
}
